import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { theme, darkTheme, expandFontTheme, useThemeContext, getThemeValue, addFontThemePrefix, } from '@contrast-security-inc/design-system-foundations';
import { css, cx } from '@emotion/css';
const lightDarkThemeBlueGray50 = 'hsl(233, 31%, 60%)';
const baseStyles = (selectedTheme, isFillTransparent, iconPlacement, iconSpacing) => css `
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  color: ${theme.color.black};
  flex-direction: ${iconPlacement === 'right' ? 'row-reverse' : 'row'};
  gap: ${iconSpacing}px;
  min-width: 60px;
  max-width: 220px;
  justify-content: center;
  outline-offset: -1px;
  ${expandFontTheme(addFontThemePrefix(selectedTheme, 'body'))};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible:enabled {
    /* stylelint-disable-next-line */
    outline: 1px solid ${getThemeValue(selectedTheme, {
    light: theme.color.neutral100,
    dark: lightDarkThemeBlueGray50,
})};
    ${selectedTheme === 'dark' &&
    `
      filter: brightness(150%);
      box-shadow: 0 0 10px 0 hsl(233deg 31% 60% / 80%);
    `}
  }

  &:hover:enabled,
  &:hover:enabled svg {
    color: ${theme.color.white};
    ${isFillTransparent
    ? `
      stroke: ${theme.color.white};
    `
    : `
      fill: ${theme.color.white};
    `}
  }
`;
const primaryStyles = (selectedTheme) => css `
  background-color: ${getThemeValue(selectedTheme, { light: theme.color.teal50, dark: darkTheme.color.prussianBlue })};
  color: ${getThemeValue(selectedTheme, { light: theme.color.black, dark: theme.color.white })};

  &:hover:enabled {
    /* stylelint-disable-next-line */
    background-color: ${getThemeValue(selectedTheme, {
    light: theme.color.turquoise90,
    dark: darkTheme.color.prussianBlue,
})};
    ${selectedTheme === 'dark' && `filter: brightness(150%);`}
  }
`;
const secondaryStyles = (selectedTheme, isFillTransparent) => css `
  background-color: ${theme.color.transparent};
  color: ${getThemeValue(selectedTheme, { light: theme.color.neutral100, dark: theme.color.neutral10 })};
  /* stylelint-disable-next-line */
  outline: 1px solid ${getThemeValue(selectedTheme, {
    light: theme.color.neutral50,
    dark: darkTheme.color.prussianBlue,
})};

  &:hover:enabled {
    /* stylelint-disable-next-line */
    background-color: ${getThemeValue(selectedTheme, {
    light: theme.color.neutral70,
    dark: darkTheme.color.blueGray100,
})};
    /* stylelint-disable-next-line */
    outline: 1px solid ${getThemeValue(selectedTheme, {
    light: theme.color.neutral70,
    dark: lightDarkThemeBlueGray50,
})};
  }

  &:focus-visible:enabled {
    /* stylelint-disable-next-line */
    background-color: ${getThemeValue(selectedTheme, {
    light: 'transparent',
    dark: darkTheme.color.blueGray100,
})};
    /* stylelint-disable-next-line */
    outline: 1px solid ${getThemeValue(selectedTheme, {
    light: theme.color.neutral100,
    dark: lightDarkThemeBlueGray50,
})};
  }

  &:focus-visible:enabled,
  &:focus-visible:enabled svg {
    /* stylelint-disable-next-line */
    color: ${getThemeValue(selectedTheme, {
    light: theme.color.turquoise90,
    dark: theme.color.neutral10,
})};
    /* stylelint-disable-next-line */
  ${isFillTransparent
    ? `
      stroke: ${getThemeValue(selectedTheme, {
        light: theme.color.turquoise90,
        dark: theme.color.neutral10,
    })};
    `
    : `
      fill: ${getThemeValue(selectedTheme, {
        light: theme.color.turquoise90,
        dark: theme.color.neutral10,
    })};
    `}
  }

  &:hover:focus-visible:enabled,
  &:hover:enabled svg {
    color: ${theme.color.white};
    ${isFillTransparent
    ? `
      stroke: ${theme.color.white};
    `
    : `
      fill: ${theme.color.white};
    `}
  }
`;
const tertiaryStyles = (selectedTheme, isFillTransparent) => css `
  background-color: ${theme.color.transparent};
  color: ${getThemeValue(selectedTheme, { light: theme.color.neutral100, dark: theme.color.neutral10 })};

  &:focus-visible:enabled {
    background-color: ${theme.color.transparent};
    color: ${getThemeValue(selectedTheme, { light: theme.color.neutral100, dark: darkTheme.color.white })};
    outline: none;
  }

  &:focus-visible:enabled svg {
    ${isFillTransparent
    ? `
      stroke: ${theme.color.white};
    `
    : `
      fill: ${theme.color.white};
    `}
  }

  &:hover:enabled {
    background-color: ${theme.color.transparent};
    color: ${getThemeValue(selectedTheme, { light: theme.color.neutral100, dark: darkTheme.color.white })};
  }
`;
const destructiveStyles = (selectedTheme, isFillTransparent) => css `
  background-color: ${theme.color.red30};

  &:focus-visible:enabled {
    background-color: ${theme.color.red70};
    color: ${theme.color.white};
    /* stylelint-disable-next-line */
    outline: 1px solid ${getThemeValue(selectedTheme, {
    light: theme.color.black,
    dark: theme.color.red70,
})};
  }

  &:focus-visible:enabled svg {
    ${isFillTransparent
    ? `
      stroke: ${theme.color.white};
    `
    : `
      fill: ${theme.color.white};
    `}
  }

  &:hover:enabled {
    background-color: ${theme.color.red70};
  }
`;
const iconStyles = css `
  display: flex;
`;
const textOverflowStyles = css `
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const getStyles = (variant, selectedTheme, isFillTransparent) => {
    switch (variant) {
        case 'primary':
            return primaryStyles(selectedTheme);
        case 'secondary':
            return secondaryStyles(selectedTheme, isFillTransparent);
        case 'tertiary':
            return tertiaryStyles(selectedTheme, isFillTransparent);
        case 'destructive':
            return destructiveStyles(selectedTheme, isFillTransparent);
    }
};
export const Button = forwardRef(({ testId, className, variant = 'primary', isDisabled, Icon, iconColor, iconPlacement = 'left', iconIsOutline = false, iconSize = 'sm', iconSpacing = 4, type = 'button', children, ...rest }, ref) => {
    const { selectedTheme } = useThemeContext();
    const iconRef = useRef(null);
    const [isFillTransparent, setIsFillTransparent] = useState(iconIsOutline);
    const classes = cx(baseStyles(selectedTheme, isFillTransparent, iconPlacement, iconSpacing), getStyles(variant, selectedTheme, isFillTransparent), className, 'ds-button');
    const primaryIconColor = (selectedTheme) => selectedTheme === 'dark' ? 'neutral10' : 'neutral100';
    const secondaryIconColor = (selectedTheme) => selectedTheme === 'dark' ? 'neutral10' : 'black';
    const getIconColor = () => {
        switch (variant) {
            case 'primary':
                return primaryIconColor(selectedTheme);
            case 'secondary':
            case 'tertiary':
                return secondaryIconColor(selectedTheme);
            case 'destructive':
                return 'black';
        }
    };
    useEffect(() => {
        if (iconRef.current) {
            const ariaLabel = iconRef.current.ariaLabel ?? 'icon';
            const isOutlineIcon = /outline([12])?$/i.test(ariaLabel);
            setIsFillTransparent(isOutlineIcon);
        }
        if (iconIsOutline) {
            setIsFillTransparent(true);
        }
    }, [iconIsOutline]);
    return (React.createElement("button", { className: classes, "data-testid": testId, ref: ref, type: type, ...rest, disabled: isDisabled },
        React.createElement("div", null, Icon && (React.createElement("span", { "data-testid": `${testId}-icon`, className: iconStyles },
            React.createElement(Icon, { size: iconSize, color: iconColor ?? getIconColor(), ref: iconRef })))),
        React.createElement("div", { className: textOverflowStyles }, children)));
});
Button.displayName = 'Button';
